<template>
  <div>
    <wa-button appearance="plain" size="m" class="assistant-trigger" @click="openDrawer">
      <PhSparkle color="white" :size="24" />
    </wa-button>

    <wa-drawer
      light-dismiss
      ref="drawer"
      class="assistant-drawer"
      placement="end"
      style="--size: 440px"
    >
      <span slot="label" class="assistant-label">
        {{ $t('sales_assistant.title') }}
        <wa-badge variant="brand" pill>Beta</wa-badge>
      </span>
      <wa-button v-if="messages.length" slot="header-actions" @click="clearChat" appearance="plain"
        ><wa-icon
          library="bootstrap"
          name="arrow-counterclockwise"
          :label="$t('sales_assistant.clear')"
        ></wa-icon
      ></wa-button>
      <div ref="messagesEl" class="assistant-messages">
        <p class="assistant-beta-hint">{{ $t('sales_assistant.beta_hint') }}</p>
        <div v-if="!messages.length" class="assistant-tags">
          <div v-for="group in TAG_GROUPS" :key="group.key" class="assistant-tags__group">
            <p class="assistant-tags__title">
              {{ $t(`sales_assistant.tags.${group.key}.title`) }}
            </p>
            <div class="assistant-tags__chips">
              <wa-button
                variant="neutral"
                appearance="outlined"
                v-for="tag in group.tags"
                :key="tag"
                size="s"
                pill
                :disabled="inFlight"
                @click="sendTag(group.key, tag)"
              >
                {{ $t(`sales_assistant.tags.${group.key}.${tag}.label`) }}
              </wa-button>
            </div>
          </div>
        </div>
        <div
          v-for="(message, index) in messages"
          :key="index"
          class="assistant-bubble"
          :class="`assistant-bubble--${message.role}`"
        >
          <template v-if="message.role === 'user'">
            <PhMicrophone
              v-if="message.inputType === 'voice'"
              :size="14"
              class="assistant-bubble__mic"
            />
            <span>{{ message.pending ? '…' : message.text }}</span>
          </template>

          <template v-else-if="message.role === 'assistant'">
            <span v-if="message.pending" class="assistant-typing">
              <span></span><span></span><span></span>
            </span>
            <template v-else>
              <!-- eslint-disable-next-line vue/no-v-html -->
              <div class="assistant-bubble__answer" v-html="formatAnswer(message.text)"></div>
              <wa-details
                v-if="message.sources.length"
                :summary="$t('sales_assistant.sources')"
                class="assistant-sources"
              >
                <div
                  v-for="(source, i) in message.sources"
                  :key="i"
                  class="assistant-sources__item"
                >
                  <strong>{{ source.title }}</strong>
                  <p v-if="source.snippet">{{ source.snippet }}</p>
                </div>
              </wa-details>
            </template>
          </template>

          <template v-else>
            <span>{{ message.text }}</span>
            <wa-button
              v-if="message.retryPayload"
              size="s"
              variant="neutral"
              appearance="outlined"
              class="assistant-retry"
              @click="retry(message)"
            >
              {{ $t('sales_assistant.retry') }}
            </wa-button>
          </template>
        </div>
      </div>

      <div slot="footer" class="assistant-footer-wrap">
        <div
          v-if="showFeedback"
          class="assistant-feedback"
          :class="{ 'assistant-feedback--done': feedbackSubmitted }"
        >
          <template v-if="feedbackSubmitted">
            <PhCheckCircle :size="16" class="assistant-feedback__check" />
            <span class="assistant-feedback__thanks">{{
              $t('sales_assistant.feedback_thanks')
            }}</span>
          </template>
          <template v-else>
            <span class="assistant-feedback__question">
              {{ $t('sales_assistant.feedback_question') }}
            </span>
            <wa-button
              class="icon-only"
              :variant="feedbackVote === true ? 'success' : 'neutral'"
              :appearance="feedbackVote === true ? 'accent' : 'outlined'"
              size="s"
              :disabled="feedbackSubmitting"
              @click="feedbackVote = true"
            >
              <PhThumbsUp slot="start" :size="16" />
            </wa-button>
            <wa-button
              class="icon-only"
              :variant="feedbackVote === false ? 'neutral' : 'neutral'"
              :appearance="feedbackVote === false ? 'accent' : 'outlined'"
              size="s"
              :disabled="feedbackSubmitting"
              @click="feedbackVote = false"
            >
              <PhThumbsDown slot="start" :size="16" />
            </wa-button>
            <wa-button
              variant="neutral"
              appearance="outlined"
              size="s"
              :disabled="feedbackSubmitting"
              @click="feedbackDialogOpen = true"
            >
              {{ $t('sales_assistant.feedback_comment') }}
              <span v-if="feedbackComment.trim()" class="assistant-feedback__commentDot"></span>
            </wa-button>
            <wa-button
              variant="brand"
              size="s"
              :loading="feedbackSubmitting"
              :disabled="feedbackVote === null || feedbackSubmitting"
              @click="submitFeedback"
            >
              {{ $t('sales_assistant.feedback_submit') }}
            </wa-button>
          </template>
        </div>
        <div class="assistant-footer">
          <wa-button
            class="assistant-talk"
            :class="{ 'assistant-talk--recording': isRecording }"
            variant="brand"
            size="l"
            :disabled="inFlight"
            @pointerdown.prevent="onHoldStart"
            @pointerup.prevent="onHoldEnd"
            @pointercancel.prevent="onHoldEnd"
          >
            <PhMicrophone :size="22" />
          </wa-button>
          <wa-input
            class="assistant-text-input"
            :placeholder="$t('sales_assistant.input_placeholder')"
            :value="textInput"
            :disabled="inFlight"
            @input="textInput = $event.target.value"
            @keyup.enter="sendText"
          ></wa-input>
          <wa-button
            class="assistant-send"
            variant="neutral"
            appearance="outlined"
            :disabled="inFlight || !textInput.trim()"
            @click="sendText"
          >
            <PhPaperPlaneRight :size="18" />
          </wa-button>
        </div>
      </div>
    </wa-drawer>

    <wa-dialog
      light-dismiss
      class="assistant-comment-dialog"
      :label="$t('sales_assistant.feedback_comment_title')"
      :open="feedbackDialogOpen"
      @wa-request-close="feedbackDialogOpen = false"
    >
      <wa-textarea
        :value="feedbackComment"
        :placeholder="$t('sales_assistant.feedback_comment_placeholder')"
        rows="5"
        resize="auto"
        @input="feedbackComment = $event.target.value"
      ></wa-textarea>
      <div slot="footer" class="assistant-comment-dialog__footer">
        <wa-button
          variant="neutral"
          appearance="outlined"
          size="s"
          @click="feedbackDialogOpen = false"
        >
          {{ $t('globals.save') }}
        </wa-button>
      </div>
    </wa-dialog>
  </div>
</template>

<script setup>
import '@awesome.me/webawesome/dist/components/badge/badge.js'
import '@awesome.me/webawesome/dist/components/button/button.js'
import '@awesome.me/webawesome/dist/components/details/details.js'
import '@awesome.me/webawesome/dist/components/dialog/dialog.js'
import '@awesome.me/webawesome/dist/components/drawer/drawer.js'
import '@awesome.me/webawesome/dist/components/input/input.js'
import '@awesome.me/webawesome/dist/components/textarea/textarea.js'
import {
  PhCheckCircle,
  PhMicrophone,
  PhPaperPlaneRight,
  PhSparkle,
  PhThumbsDown,
  PhThumbsUp
} from '@phosphor-icons/vue'
import { createClient } from '@supabase/supabase-js'
import { computed, inject, nextTick, reactive, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useDeviceStore } from '@/stores/device'
import { useNotification } from '@/composables/useNotification.js'
import { useVoiceInput } from '@/composables/useVoiceInput.js'

const injectedSupabase = inject('supabase')
const deviceStore = useDeviceStore()

// Local testing: point only the assistant at a local `supabase functions serve`
// instance while the rest of the app stays on the hosted project (same pattern
// as the test client in useLensConfigurator).
const localSupabaseUrl = null
const supabase = localSupabaseUrl
  ? createClient(localSupabaseUrl, import.meta.env.VITE_SALES_ASSISTANT_SUPABASE_ANON_KEY)
  : injectedSupabase
const { t } = useI18n()
const { showNotification } = useNotification()
const { isRecording, error: voiceError, start, stop } = useVoiceInput()

const drawer = ref()
const messagesEl = ref()
const messages = ref([])
const textInput = ref('')
const inFlight = ref(false)

const feedbackVote = ref(null)
const feedbackComment = ref('')
const feedbackDialogOpen = ref(false)
const feedbackSubmitting = ref(false)
const feedbackSubmitted = ref(false)

// The strip appears once the first answer has completed and stays for the
// rest of the conversation (locked after submit); clearing the chat re-arms it.
const showFeedback = computed(() =>
  messages.value.some((m) => m.role === 'assistant' && !m.pending)
)

function openDrawer() {
  drawer.value.open = true
}

// Late responses of an in-flight request mutate objects that are no longer in
// the list — harmless, so no explicit abort needed for the prototype.
function clearChat() {
  messages.value = []
  feedbackVote.value = null
  feedbackComment.value = ''
  feedbackDialogOpen.value = false
  feedbackSubmitting.value = false
  feedbackSubmitted.value = false
}

async function submitFeedback() {
  if (feedbackVote.value === null || feedbackSubmitting.value || feedbackSubmitted.value) return

  feedbackSubmitting.value = true
  try {
    const history = messages.value
      .filter((m) => m.role !== 'error')
      .map((m) => ({
        role: m.role,
        ...(m.inputType && { inputType: m.inputType }),
        text: m.text,
        ...(m.sources?.length && { sources: m.sources }),
        ...(m.queryId && { queryId: m.queryId })
      }))
    // The strip rates the conversation as a whole; link it to the latest
    // answer so the row joins to that query in sales_assistant_queries.
    const lastAnswer = [...messages.value]
      .reverse()
      .find((m) => m.role === 'assistant' && !m.pending)

    const { data: sessionData } = await injectedSupabase.auth.getSession()
    const accessToken = sessionData?.session?.access_token
    const { error } = await supabase.functions.invoke('sales-assistant', {
      body: {
        feedback: {
          helpful: feedbackVote.value,
          comment: feedbackComment.value.trim() || null,
          query_id: lastAnswer?.queryId ?? null,
          history
        },
        employeeEid: deviceStore.settings.currentEmployee?.eid ?? null,
        country: deviceStore.settings.country,
        storeCode: deviceStore.settings.warehouse
      },
      ...(accessToken && { headers: { Authorization: `Bearer ${accessToken}` } })
    })
    if (error) throw error

    feedbackSubmitted.value = true
  } catch (e) {
    console.error('sales-assistant feedback failed:', e)
    showNotification({
      message: t('sales_assistant.feedback_error'),
      variant: 'danger',
      icon: 'exclamation-octagon'
    })
  } finally {
    feedbackSubmitting.value = false
  }
}

watch(
  () => messages.value.length,
  async () => {
    await nextTick()
    messagesEl.value?.scrollTo({ top: messagesEl.value.scrollHeight, behavior: 'smooth' })
  }
)

// Gemini answers use light markdown (bold, lists). Render it safely: escape
// HTML first, then transform only the constructs we expect.
function formatAnswer(text) {
  const escaped = (text ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    // the model occasionally collapses all newlines into one line — reinsert
    // breaks at the glued seams (no false positives: healthy text always has
    // a space or newline at these spots)
    .replace(/\*\*-[ \t]+/g, '**\n- ') // "…**- item" → bullet on its own line
    .replace(/\*\*\*\*(?=[^*\n])/g, '**\n\n**') // "…€****Header" → new block
    .replace(/([.!?])\*\*(?=[^*\n])/g, '$1\n\n**') // "satz.**Header" → new block
    .replace(/([.!?)])-[ \t]+(?=\S)/g, '$1\n- ') // "satz.- item" / ")- item" → bullet line
    .replace(/:\*\*(?=\S)/g, ':**\n') // "…):**Inhalt" → break after bold header

  const inline = (line) =>
    line
      .replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
      // italics: the opening * must sit on a boundary and the closing * must not
      // be inside a word, otherwise German gender stars (Kund*innen) bleed into
      // the next emphasis on the same line
      .replace(/(^|[\s(„"'>])\*(?!\*)([^*\n]+?)\*(?![\w*])/g, '$1<em>$2</em>')
      .replace(/`([^`]+)`/g, '<code>$1</code>')

  const blocks = []
  let list = null
  const flushList = () => {
    if (!list) return
    const lis = list.items
      .map((item) =>
        item.children.length
          ? `<li>${item.text}<ul>${item.children.map((c) => `<li>${c}</li>`).join('')}</ul></li>`
          : `<li>${item.text}</li>`
      )
      .join('')
    blocks.push(`<${list.tag}>${lis}</${list.tag}>`)
    list = null
  }

  for (const rawLine of escaped.split('\n')) {
    const line = rawLine.trim()
    const indent = rawLine.match(/^[ \t]*/)[0].length
    const numbered = line.match(/^\d+[.)]\s+(.*)/)
    // models sometimes emit list items as indented plain lines or as
    // "**Label**: …" paragraphs — treat both as bullets
    const indented = !numbered && indent >= 2 && line ? [null, line] : null
    const labeled = line.match(/^\*\*[^*]+\*\*:/) ? [null, line] : null
    const bullet = line.match(/^[-*•]\s+(.*)/) || indented || labeled
    if (bullet || numbered) {
      const text = inline(bullet ? bullet[1] : numbered[1])
      // indented item while a list is open → sub-bullet of the last item. Any
      // indent counts: the edge function normalizes to two spaces, but older
      // answers and prompt drift use one.
      if (list && indent >= 1 && list.items.length) {
        list.items[list.items.length - 1].children.push(text)
        continue
      }
      const tag = bullet ? 'ul' : 'ol'
      if (!list || list.tag !== tag) {
        flushList()
        list = { tag, items: [] }
      }
      list.items.push({ text, children: [] })
      continue
    }
    flushList()
    if (!line) continue
    // "**Kurzantwort**" / "**Details**" on its own line = section header
    const section = line.match(/^\*\*([^*]+?)\*\*:?$/)
    if (section) {
      blocks.push(`<h4 class="assistant-answer__section">${section[1]}</h4>`)
      continue
    }
    blocks.push(`<p>${inline(line)}</p>`)
  }
  flushList()
  return blocks.join('')
}

function blobToBase64(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(reader.result.split(',')[1])
    reader.onerror = reject
    reader.readAsDataURL(blob)
  })
}

async function onHoldStart() {
  if (inFlight.value) return
  await start()
  if (voiceError.value) {
    messages.value.push({
      role: 'error',
      text:
        voiceError.value === 'permission'
          ? t('sales_assistant.error_mic_permission')
          : t('sales_assistant.error_upstream')
    })
  }
}

async function onHoldEnd() {
  const blob = await stop()
  if (!blob) return

  const audio = await blobToBase64(blob)
  // reactive() so later mutations (transcript, pending) trigger re-renders
  const userMessage = reactive({ role: 'user', inputType: 'voice', text: '', pending: true })
  messages.value.push(userMessage)
  await send(
    {
      audio,
      mimeType: blob.type,
      country: deviceStore.settings.country,
      storeCode: deviceStore.settings.warehouse
    },
    userMessage
  )
}

// Starter chips: each key resolves to a label + full canned question in the
// locale files, so the question language follows the UI language.
const TAG_GROUPS = [
  { key: 'offers', tags: ['viu_plus', 'package_deal', 'pd_example', 'prices', 'conscious_club'] },
  { key: 'processes', tags: ['eye_test', 'pickup', 'fitting', 'store_check'] },
  { key: 'aftersales', tags: ['pressure_fit', 'returns', 'insurance_claim', 'intolerance'] },
  { key: 'lenses', tags: ['lens_types', 'coatings', 'progressive', 'sun_rx'] }
]

function sendTag(groupKey, tagKey) {
  submitQuestion(t(`sales_assistant.tags.${groupKey}.${tagKey}.question`))
}

async function sendText() {
  const text = textInput.value.trim()
  if (!text) return
  textInput.value = ''
  await submitQuestion(text)
}

async function submitQuestion(text) {
  if (inFlight.value) return
  const userMessage = reactive({ role: 'user', inputType: 'text', text, pending: false })
  messages.value.push(userMessage)
  await send(
    { text, country: deviceStore.settings.country, storeCode: deviceStore.settings.warehouse },
    userMessage
  )
}

async function send(payload, userMessage) {
  inFlight.value = true
  const assistantMessage = reactive({
    role: 'assistant',
    pending: true,
    text: '',
    sources: [],
    queryId: null
  })
  messages.value.push(assistantMessage)

  try {
    // Always authenticate with the real Wings session so the query log gets
    // the actual user — the local override client itself has no session.
    const { data: sessionData } = await injectedSupabase.auth.getSession()
    const accessToken = sessionData?.session?.access_token
    const { data, error } = await supabase.functions.invoke('sales-assistant', {
      body: payload,
      ...(accessToken && { headers: { Authorization: `Bearer ${accessToken}` } })
    })
    if (error) throw error

    const result = typeof data === 'string' ? JSON.parse(data) : data
    if (payload.audio) {
      userMessage.text = result.transcript || '…'
      userMessage.pending = false
    }
    assistantMessage.text = result.answer
    assistantMessage.sources = result.sources || []
    assistantMessage.queryId = result.query_id || null
    assistantMessage.pending = false
  } catch (e) {
    console.error('sales-assistant request failed:', e)
    messages.value = messages.value.filter((m) => m !== assistantMessage)
    if (payload.audio) userMessage.pending = false
    messages.value.push({
      role: 'error',
      text: t('sales_assistant.error_upstream'),
      retryPayload: payload,
      retryUserMessage: userMessage
    })
  } finally {
    inFlight.value = false
  }
}

async function retry(errorMessage) {
  if (inFlight.value) return
  messages.value = messages.value.filter((m) => m !== errorMessage)
  if (errorMessage.retryUserMessage?.inputType === 'voice') {
    errorMessage.retryUserMessage.pending = true
  }
  await send(errorMessage.retryPayload, errorMessage.retryUserMessage)
}
</script>

<style scoped>
.assistant-trigger::part(label) {
  display: flex;
  align-items: center;
  justify-content: center;
}

.assistant-label {
  display: inline-flex;
  align-items: center;
  gap: var(--wa-space-xs);
}

.assistant-label wa-badge {
  font-size: var(--wa-font-size-2xs);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  /* Wings themes --wa-color-brand-* to greys — force the beta blue */
  background-color: rgb(59 130 246);
  border-color: rgb(59 130 246);
  color: #fff;
}

.assistant-beta-hint {
  margin: 0 0 var(--wa-space-s);
  font-size: var(--wa-font-size-xs);
  color: var(--wa-color-neutral-50);
  text-align: center;
}

.assistant-messages {
  display: flex;
  flex-direction: column;
  gap: var(--wa-space-s);
  min-height: 100%;
  justify-content: flex-end;
}

.assistant-tags__group {
  margin-bottom: var(--wa-space-m);
}

.assistant-tags__title {
  margin: 0 0 var(--wa-space-xs);
  font-size: var(--wa-font-size-s);
  font-weight: var(--wa-font-weight-semibold);
  opacity: 0.6;
}

.assistant-tags__chips {
  display: flex;
  flex-wrap: wrap;
  gap: var(--wa-space-xs);
}

.assistant-bubble {
  max-width: 85%;
  padding: var(--wa-space-s) var(--wa-space-m);
  border-radius: var(--wa-border-radius-l);
  line-height: 1.5;
  white-space: pre-wrap;
}

.assistant-bubble--user {
  align-self: flex-end;
  background-color: #000;
  color: #fff;
}

.assistant-bubble__mic {
  margin-right: 4px;
  opacity: 0.7;
}

.assistant-bubble--assistant {
  align-self: flex-start;
  max-width: 95%; /* long structured answers need the width */
  background-color: var(--wa-color-neutral-90);
  white-space: normal; /* formatAnswer generates block markup */
}

/* formatAnswer output is injected with v-html, so scoped styles only reach it
   through :deep() — without it the answer renders unstyled */
.assistant-bubble__answer :deep(p),
.assistant-bubble__answer :deep(ul),
.assistant-bubble__answer :deep(ol) {
  margin: 0 0 var(--wa-space-s);
}

.assistant-bubble__answer :deep(p:last-child),
.assistant-bubble__answer :deep(ul:last-child),
.assistant-bubble__answer :deep(ol:last-child) {
  margin-bottom: 0;
}

/* section headers: "Kurzantwort" / "Details" / "Nächster Schritt" */
.assistant-bubble__answer :deep(.assistant-answer__section) {
  margin: var(--wa-space-m) 0 var(--wa-space-xs);
  font-size: var(--wa-font-size-xs);
  font-weight: var(--wa-font-weight-bold);
  text-transform: uppercase;
  letter-spacing: 0.6px;
  color: var(--wa-color-neutral-50);
}

.assistant-bubble__answer :deep(.assistant-answer__section:first-child) {
  margin-top: 0;
}

/* the global reset.css strips list-style — restore it inside answers */
.assistant-bubble__answer :deep(ul),
.assistant-bubble__answer :deep(ol) {
  padding-left: 1.3em;
}

.assistant-bubble__answer :deep(ul) {
  list-style: disc;
}

.assistant-bubble__answer :deep(ol) {
  list-style: decimal;
}

.assistant-bubble__answer :deep(li) {
  margin-bottom: var(--wa-space-xs);
}

.assistant-bubble__answer :deep(li ul) {
  list-style: circle;
  margin: 4px 0 0;
  padding-left: 1.2em;
}

.assistant-bubble__answer :deep(li ul li) {
  margin-bottom: 2px;
}

/* inline *Fokus:* labels — de-emphasised rather than italic-loud */
.assistant-bubble__answer :deep(em) {
  font-style: normal;
  font-weight: var(--wa-font-weight-semibold);
  color: var(--wa-color-neutral-40);
}

.assistant-bubble--error {
  align-self: center;
  background-color: var(--wa-color-danger-90);
  text-align: center;
}

.assistant-retry {
  display: block;
  margin-top: var(--wa-space-xs);
}

.assistant-sources {
  margin-top: var(--wa-space-xs);
  font-size: var(--wa-font-size-s);
}

.assistant-sources__item {
  margin-bottom: var(--wa-space-xs);
}

.assistant-sources__item p {
  margin: 2px 0 0;
  opacity: 0.7;
}

.assistant-footer-wrap {
  display: flex;
  flex-direction: column;
  gap: var(--wa-space-s);
}

.assistant-feedback {
  display: flex;
  align-items: center;
  gap: var(--wa-space-xs);
  padding-bottom: var(--wa-space-s);
  border-bottom: 1px solid var(--wa-color-neutral-80);
}

.assistant-feedback--done {
  justify-content: center;
  color: var(--wa-color-success-40);
  font-size: var(--wa-font-size-s);
}

.assistant-feedback__check {
  flex-shrink: 0;
}

.assistant-feedback__question {
  margin-right: auto;
  color: var(--wa-color-neutral-50);
  font-size: var(--wa-font-size-s);
  text-align: left;
  line-height: 1.3;
}

.assistant-feedback__commentDot {
  display: inline-block;
  width: 6px;
  height: 6px;
  margin-left: 4px;
  border-radius: 50%;
  /* same forced beta blue as the badge */
  background-color: rgb(59 130 246);
}

.assistant-comment-dialog__footer {
  display: flex;
  justify-content: flex-end;
  width: 100%;
}

.assistant-footer {
  display: flex;
  gap: var(--wa-space-s);
  align-items: center;
}

/* Phosphor icons are svg elements on the text baseline — center them in the
   icon-only buttons */
.assistant-talk::part(label),
.assistant-send::part(label) {
  display: flex;
  align-items: center;
  justify-content: center;
}

.assistant-text-input {
  flex: 1;
}

.assistant-talk--recording::part(base) {
  animation: assistant-pulse 1.2s ease-in-out infinite;
  background-color: var(--wa-color-danger-40);
  border-color: var(--wa-color-danger-40);
}

@keyframes assistant-pulse {
  0%,
  100% {
    box-shadow: 0 0 0 0 rgba(220, 38, 38, 0.5);
  }
  50% {
    box-shadow: 0 0 0 10px rgba(220, 38, 38, 0);
  }
}

.assistant-typing {
  display: inline-flex;
  gap: 4px;
}

.assistant-typing span {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background-color: var(--wa-color-neutral-50);
  animation: assistant-blink 1.2s infinite;
}

.assistant-typing span:nth-child(2) {
  animation-delay: 0.2s;
}

.assistant-typing span:nth-child(3) {
  animation-delay: 0.4s;
}

@keyframes assistant-blink {
  0%,
  80%,
  100% {
    opacity: 0.3;
  }
  40% {
    opacity: 1;
  }
}
</style>
